import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";
import { NavActions } from "../components/NavActions.js";
import { Skeleton } from "../components/ui/Skeleton.js";
import { BookSearchDialog } from "../features/books/BookSearchDialog.js";

type ReadStatus = "WANT_TO_READ" | "READING" | "READ";

const STATUS_ORDER: ReadStatus[] = ["READING", "WANT_TO_READ", "READ"];

function StarRating({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button
          key={n}
          className="text-xs w-4 h-4 rounded-sm transition-colors"
          style={{
            background: value !== null && n <= value ? "var(--color-accent)" : "var(--color-secondary)",
            color: value !== null && n <= value ? "white" : "var(--color-muted-foreground)",
          }}
          onClick={() => onChange(value === n ? null : n)}
          title={`${n}/10`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: "var(--color-accent)" }}>
      {children}
    </p>
  );
}

function BookItem({
  item,
}: {
  item: {
    id: string;
    bookId: string;
    book: { id: string; title: string; authors: string[]; coverUrl: string | null };
    status: ReadStatus;
    rating: number | null;
    notes: string | null;
    updatedAt: Date | string;
  };
}) {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes ?? "");
  const [saving, setSaving] = useState(false);

  const update = trpc.readingList.update.useMutation({
    onMutate: async (vars) => {
      await utils.readingList.list.cancel();
      const prev = utils.readingList.list.getData({});
      utils.readingList.list.setData({}, (old) =>
        old?.map((i) => i.id === vars.userBookId ? { ...i, ...vars } : i)
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.readingList.list.setData({}, ctx.prev); },
    onSettled: () => utils.readingList.list.invalidate(),
  });
  const remove = trpc.readingList.remove.useMutation({
    onMutate: async (vars) => {
      await utils.readingList.list.cancel();
      const prev = utils.readingList.list.getData({});
      utils.readingList.list.setData({}, (old) => old?.filter((i) => i.id !== vars.userBookId));
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.readingList.list.setData({}, ctx.prev); },
    onSettled: () => utils.readingList.list.invalidate(),
  });

  function handleStatusChange(status: ReadStatus) {
    update.mutate({ userBookId: item.id, status });
  }

  function handleRatingChange(rating: number | null) {
    update.mutate({ userBookId: item.id, rating });
  }

  function handleSaveNotes() {
    setSaving(true);
    update.mutate(
      { userBookId: item.id, notes: notes || null },
      { onSettled: () => setSaving(false) }
    );
  }

  return (
    <li
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-xs)" }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {item.book.coverUrl ? (
          <img
            src={item.book.coverUrl}
            alt=""
            className="w-9 h-12 object-cover rounded flex-shrink-0"
            style={{ background: "var(--color-muted)" }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <div className="w-9 h-12 rounded flex-shrink-0" style={{ background: "var(--color-muted)" }} />
        )}

        <div className="flex-1 min-w-0">
          <p className="font-serif text-sm font-medium truncate" style={{ color: "var(--color-foreground)" }}>
            {item.book.title}
          </p>
          <p className="text-xs truncate mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
            {item.book.authors.join(", ")}
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <select
              className="text-xs rounded px-1.5 py-0.5 transition-all"
              style={{
                border: "1px solid var(--color-border)",
                background: "var(--color-background)",
                color: "var(--color-foreground)",
              }}
              value={item.status}
              onChange={(e) => handleStatusChange(e.target.value as ReadStatus)}
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{t(`readingList.status.${s}`)}</option>
              ))}
            </select>
            {item.status === "READ" && (
              <StarRating value={item.rating} onChange={handleRatingChange} />
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            className="text-xs px-2 py-1 rounded transition-colors"
            style={{ color: "var(--color-muted-foreground)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-foreground)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "▲" : t("readingList.notes")}
          </button>
          <button
            className="text-xs px-2 py-1 rounded transition-colors"
            style={{ color: "var(--color-muted-foreground)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-destructive)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
            onClick={() => { if (confirm(t("readingList.removeBook"))) remove.mutate({ userBookId: item.id }); }}
          >
            {t("common.remove")}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t" style={{ borderColor: "var(--color-border)" }}>
          <textarea
            className="w-full rounded-lg px-3 py-2 text-sm mt-3 focus:outline-none transition-all"
            style={{
              border: "1px solid var(--color-border)",
              background: "var(--color-background)",
              color: "var(--color-foreground)",
              resize: "vertical",
            }}
            rows={3}
            placeholder="Your notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          />
          <Button size="sm" loading={saving} onClick={handleSaveNotes}>{t("readingList.saveNotes")}</Button>
        </div>
      )}
    </li>
  );
}

export function ReadingList() {
  const [showSearch, setShowSearch] = useState(false);
  const [activeStatus, setActiveStatus] = useState<ReadStatus | undefined>(undefined);
  const { t } = useTranslation();
  const { data: items, isLoading } = trpc.readingList.list.useQuery({ status: activeStatus });
  const utils = trpc.useUtils();

  const grouped = items
    ? STATUS_ORDER.filter((s) => !activeStatus || s === activeStatus).map((status) => ({
        status,
        books: items.filter((i) => i.status === status),
      })).filter((g) => g.books.length > 0)
    : [];

  const filterLabels: Record<string, string> = {
    all: t("readingList.filters.all"),
    READING: t("readingList.filters.reading"),
    WANT_TO_READ: t("readingList.filters.wantToRead"),
    READ: t("readingList.filters.read"),
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Link
          to="/dashboard"
          className="text-sm flex-shrink-0"
          style={{ color: "var(--color-muted-foreground)", textDecoration: "none" }}
        >
          {t("nav.backToDashboard")}
        </Link>
        <span style={{ color: "var(--color-border-strong)" }}>|</span>
        <span className="font-serif font-semibold" style={{ color: "var(--color-foreground)" }}>
          {t("readingList.title")}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex gap-1">
            {([undefined, ...STATUS_ORDER] as (ReadStatus | undefined)[]).map((s) => (
              <button
                key={s ?? "all"}
                className="text-xs px-2.5 py-1 rounded-full transition-all"
                style={{
                  background: activeStatus === s ? "var(--color-accent)" : "var(--color-secondary)",
                  color: activeStatus === s ? "white" : "var(--color-muted-foreground)",
                }}
                onClick={() => setActiveStatus(s)}
              >
                {filterLabels[s ?? "all"]}
              </button>
            ))}
          </div>
          <NavActions />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10 space-y-10 md:px-10">
        <div className="flex justify-end" style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both" }}>
          <Button onClick={() => setShowSearch(true)}>{t("readingList.addBook")}</Button>
        </div>

        {isLoading && (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                <Skeleton style={{ width: "2.25rem", height: "3rem", flexShrink: 0 }} />
                <div className="flex-1 space-y-1.5">
                  <Skeleton style={{ width: "50%", height: "0.85rem" }} />
                  <Skeleton style={{ width: "30%", height: "0.75rem" }} />
                  <Skeleton style={{ width: "5rem", height: "1.25rem", borderRadius: "0.25rem" }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && items?.length === 0 && (
          <div
            className="rounded-xl px-5 py-10 text-center"
            style={{ background: "var(--color-card)", border: "1px dashed var(--color-border-strong)" }}
          >
            <p className="font-serif italic text-sm" style={{ color: "var(--color-muted-foreground)" }}>
              {t("readingList.empty")}
            </p>
          </div>
        )}

        {grouped.map(({ status, books }) => (
          <div key={status} style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}>
            <SectionLabel>
              {t(`readingList.status.${status}`)} ({books.length})
            </SectionLabel>
            <ul className="space-y-2">
              {books.map((item) => (
                <BookItem key={item.id} item={item as any} />
              ))}
            </ul>
          </div>
        ))}
      </main>

      {showSearch && (
        <BookSearchDialog
          readingListMode
          onClose={() => setShowSearch(false)}
          onAdded={() => {
            utils.readingList.list.invalidate();
            setShowSearch(false);
          }}
        />
      )}
    </div>
  );
}
